/**
 * Emergency fire mode — the operator surface for a freeze that, until
 * now, could be raised by a lift and never seen, explained or ended
 * from this product at all.
 *
 * Mounted from `FireAlarmProvider` so it is reachable from anywhere in
 * the shell (the status banner while an alarm is running, the top bar
 * otherwise). It is a dialog rather than a route on purpose: nobody
 * navigates to a screen during an evacuation, and a route would be one
 * more thing to find while the overlay is strobing.
 *
 * THREE THINGS IT HAS TO GET RIGHT
 *
 * 1. The drill flag is the loudest control in the trigger form and it
 *    starts ON. A drill is how a hotel exercises this safely, so the
 *    safe option is the default and a real building-wide freeze is a
 *    deliberate second action, never a mis-click.
 *
 * 2. The recovery choice states its consequence in the operator's own
 *    units, from the server's own numbers.
 *    `FireModeState.interrupted_task_count` is the number of tasks RMF
 *    has paused for the alarm right now, and `queued_task_count` the
 *    work stacked up behind the freeze — both computed by
 *    `fire_mode.py::state` through the SAME function `resolve` builds
 *    its cancel list with, so the count on the cancel button is the
 *    count the button acts on — which is the entire reason the dialog
 *    quotes them instead of counting tasks itself.
 *
 *    RESUME SENDS NO PER-TASK COMMAND. RMF pauses and resumes tasks
 *    itself at the emergency (platform change of 2026-09-16, dropping
 *    the per-task `task.interrupt`/`task.resume` in fire mode), so a
 *    resume lifts the freeze and recommissions; the paused tasks come
 *    back on their own. A robot the Gausium adapter held mid-ride is
 *    the exception, and it is released from its Robot drawer only.
 *
 * 3. A partial failure is shown, not swallowed. `resolve` reports
 *    per-task success and failure precisely because an operator told
 *    "resumed" when three tasks did not come back is worse off than one
 *    told nothing.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  useEmergencyCapabilities,
  useFireModeState,
  useResolveFireMode,
  useTriggerFireMode,
} from '../../api/hooks/useFireMode';
import { formatDateTime } from '../../utils/date-format';
import type { FireEvent, FireResolution, FireResolveResponse } from '../../api/v1';
import { fireErrorText } from './fire-error-text';
import './FireModePanel.css';

function triggerSummary(event: FireEvent): string {
  const who =
    event.trigger_source === 'lift'
      ? `Lift ${event.trigger_detail ?? 'unknown'} recalled`
      : 'Triggered by an operator';
  return `${who}, ${formatDateTime(event.triggered_at_ms)}`;
}

/** Confirm-in-place: the button becomes its own consequence sentence
 *  plus a confirm/cancel pair. A portalled ConfirmDialog would sit on
 *  top of this dialog, and stacked modals during an evacuation are how
 *  people lose track of what they just agreed to. */
function ConfirmAction({
  label,
  consequence,
  confirmLabel,
  variant,
  pending,
  disabled,
  testId,
  onConfirm,
}: {
  label: string;
  consequence: string;
  confirmLabel: string;
  variant: 'danger' | 'primary';
  pending: boolean;
  disabled?: boolean;
  testId: string;
  onConfirm: () => void;
}) {
  const [armed, setArmed] = useState(false);
  const backRef = useRef<HTMLButtonElement | null>(null);

  // ARMED: focus goes to Back, the safe choice. The button that was
  // pressed unmounts as it arms, which dropped keyboard focus to the
  // page behind the dialog. And Escape steps back out of the confirm
  // before it closes the panel: one key press should never take an
  // operator from "about to freeze the site" to "dialog gone" without
  // showing them where they are. Registered on window in the capture
  // phase, so it runs before the panel's own Escape listener.
  useEffect(() => {
    if (!armed) return undefined;
    backRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      e.stopImmediatePropagation();
      setArmed(false);
    }
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [armed]);

  if (!armed) {
    return (
      <button
        type="button"
        className={`nx-fire-panel__btn nx-fire-panel__btn--${variant}`}
        onClick={() => setArmed(true)}
        disabled={disabled || pending}
        data-testid={testId}
      >
        {label}
      </button>
    );
  }

  return (
    <div className="nx-fire-panel__confirm" data-testid={`${testId}-confirm`}>
      <p className="nx-fire-panel__consequence">{consequence}</p>
      <div className="nx-fire-panel__confirm-row">
        <button
          type="button"
          // SOLID at the commit step: the tinted fill the first button
          // wears read, in light mode, as a pale pink box the same weight
          // as Back -- the destructive step did not look like one.
          className={`nx-fire-panel__btn nx-fire-panel__btn--${variant} nx-fire-panel__btn--commit`}
          onClick={() => {
            setArmed(false);
            onConfirm();
          }}
          disabled={pending}
          data-testid={`${testId}-confirm-btn`}
        >
          {pending ? 'Sending…' : confirmLabel}
        </button>
        <button
          ref={backRef}
          type="button"
          className="nx-fire-panel__btn nx-fire-panel__btn--quiet"
          onClick={() => setArmed(false)}
          disabled={pending}
          data-testid={`${testId}-back`}
        >
          Back
        </button>
      </div>
    </div>
  );
}

/**
 * What the panel says in place of a control the reader does not have,
 * or while it is still finding out.
 *
 * Every role reaches this panel with `g x` (decided 2026-10-31: anyone
 * may check the fire state in an alarm, only holders may act), so these
 * sentences are read by the front desk as often as by supervisors. They
 * used to name the capability code (`emergency.trigger`), and while the
 * matrix was still loading a supervisor saw that same refusal flash up
 * before their buttons appeared: `!loaded` took the gated branch.
 *
 * A failed read is answered too (review BC-F13): `useEmergencyCapabilities`
 * then reads the role mirror, so a supervisor gets the controls (the
 * server still refuses what it must) and everyone else their sentence.
 * It used to say "Couldn't check what you can do here" and offer a
 * supervisor in a real alarm no by-hand trigger at all (D7).
 */
function GatedNote({
  known,
  testId,
  children,
}: {
  /** The matrix answered, or failed and the role mirror answered. */
  known: boolean;
  testId: string;
  children: ReactNode;
}) {
  return (
    <p className="nx-fire-panel__gated" data-testid={testId}>
      {known ? children : 'Checking what you can do here…'}
    </p>
  );
}

/** "1 task", "5 tasks": the panel's counts said "task(s)" (review
 *  BC-F14, 2026-10-31). */
function count(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`;
}

/** The cancel button, named for how many it cancels. */
function cancelAllLabel(n: number): string {
  if (n === 0) return 'Cancel all tasks';
  return n === 1 ? 'Cancel the 1 task' : `Cancel all ${n} tasks`;
}

function ResolutionReport({ result }: { result: FireResolveResponse }) {
  const failed = result.failed_task_ids;
  // A resume sends no per-task command, so its `succeeded_task_ids` is
  // empty by design — "0 task(s) accepted the command" would read as
  // nothing having worked. Say what actually happened instead. A
  // cancel_all, and any answer that does name accepted tasks, keeps
  // the count.
  const perTaskLine =
    result.event.resolution === 'resume' && result.succeeded_task_ids.length === 0
      ? 'Tasks paused for the alarm carry on by themselves.'
      : `${count(result.succeeded_task_ids.length, 'task')} accepted the command.`;
  return (
    <div
      className={`nx-fire-panel__report${failed.length ? ' nx-fire-panel__report--partial' : ''}`}
      role="status"
      data-testid="fire-recovery-report"
    >
      <strong>
        {failed.length
          ? 'Recovery finished with failures.'
          : 'Recovery finished. The site is running again.'}
      </strong>
      <ul>
        <li>{perTaskLine}</li>
        <li>{count(result.recommissioned_robots.length, 'robot')} back in service.</li>
        {failed.length > 0 && (
          <li className="nx-fire-panel__failed">
            {count(failed.length, 'task')} did NOT come back and need someone to deal with them
            in Tasks: {failed.join(', ')}
          </li>
        )}
      </ul>
    </div>
  );
}

function TriggerForm({ onDone }: { onDone: () => void }) {
  const { canTrigger, loaded, failed } = useEmergencyCapabilities();
  const trigger = useTriggerFireMode();
  const [drill, setDrill] = useState(true);
  const [notes, setNotes] = useState('');

  if (!(loaded || failed) || !canTrigger) {
    // The minute is D7's safe step, written in as a normal one: if the
    // building's alarm does not reach the console, a supervisor starts
    // fire mode by hand. Worded around what every device shows, the red
    // bar at the top: the front desk's iPad and phones have no keyboard
    // for `g x`, so "this still says No fire event" was a check most of
    // them could not make (review BC-F12, 2026-10-31).
    return (
      <GatedNote known={loaded || failed} testId="fire-trigger-gated">
        Only a supervisor can start fire mode or a drill. A real alarm from the building&apos;s
        fire panel starts it by itself. If the alarm is sounding and no red Fire alarm active bar
        shows at the top of the console after a minute, tell a supervisor.
      </GatedNote>
    );
  }

  return (
    <div className="nx-fire-panel__trigger">
      <label
        className={`nx-fire-panel__drill${drill ? ' nx-fire-panel__drill--on' : ''}`}
        data-testid="fire-drill-toggle"
      >
        <input
          type="checkbox"
          checked={drill}
          onChange={(e) => setDrill(e.target.checked)}
          data-testid="fire-drill-checkbox"
        />
        <span className="nx-fire-panel__drill-text">
          <strong>{drill ? 'DRILL' : 'REAL EVENT — NOT A DRILL'}</strong>
          <em>
            {drill
              ? 'Recorded and announced as a drill. The freeze is real either way: robots stop and dispatch is refused until you resolve it.'
              : 'Recorded as a genuine fire event. Use this only when the building is actually being evacuated.'}
          </em>
        </span>
      </label>

      <label className="nx-fire-panel__field">
        <span>Notes (optional)</span>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. quarterly drill, level 3"
          data-testid="fire-trigger-notes"
        />
      </label>

      <ConfirmAction
        label={drill ? 'Start fire drill' : 'Trigger fire mode'}
        consequence={
          drill
            ? 'Every running task will be interrupted, and robots drive to a parking spot on their floor (or stop where they are if it has none). Every idle robot is taken out of service, exactly as in a real event. Dispatch stays refused until you resolve it from this panel.'
            : 'This freezes the whole site. Every running task is interrupted, and robots drive to a parking spot on their floor (or stop where they are if it has none). Every idle robot is taken out of service, and dispatch is refused until a supervisor resolves it here.'
        }
        confirmLabel={drill ? 'Start the drill' : 'Freeze the site'}
        variant={drill ? 'primary' : 'danger'}
        pending={trigger.isPending}
        testId="fire-trigger"
        onConfirm={() => {
          trigger.mutate(
            { drill, ...(notes ? { notes } : {}) },
            { onSuccess: () => onDone() },
          );
        }}
      />

      {trigger.isError && (
        <p className="nx-fire-panel__error" role="alert" data-testid="fire-trigger-error">
          {fireErrorText(trigger.error, 'trigger')}
        </p>
      )}
    </div>
  );
}

function RecoveryForm({
  event,
  onResolved,
}: {
  event: { interrupted: number; queued: number };
  onResolved: (r: FireResolveResponse) => void;
}) {
  const { canReset, loaded, failed } = useEmergencyCapabilities();
  const resolve = useResolveFireMode();
  const [notes, setNotes] = useState('');

  if (!(loaded || failed) || !canReset) {
    return (
      <GatedNote known={loaded || failed} testId="fire-recovery-gated">
        Only a supervisor can end fire mode. They choose here whether the paused and queued tasks
        above carry on or are cancelled. Ask your supervisor.
      </GatedNote>
    );
  }

  const send = (resolution: FireResolution) =>
    resolve.mutate(
      { resolution, ...(notes ? { notes } : {}) },
      { onSuccess: (r) => onResolved(r) },
    );

  return (
    <div className="nx-fire-panel__recovery">
      <label className="nx-fire-panel__field">
        <span>Reset notes (optional)</span>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. panel reset by building engineer, all clear"
          data-testid="fire-resolve-notes"
        />
      </label>

      <div className="nx-fire-panel__choices">
        <div className="nx-fire-panel__choice">
          <ConfirmAction
            label="Resume work"
            consequence={
              // "Ends", not "Lifts": in a lift-heavy hotel the verb read
              // as the lifts; "robot panel", as the rest of the copy says
              // (review BC-F14, 2026-10-31).
              'Ends the freeze. Tasks paused for the alarm carry on by themselves. ' +
              'A robot that was in a lift when the alarm started stays held until ' +
              'someone confirms its floor in its robot panel (Robots, then the robot, then ' +
              'Confirm floor and release).'
            }
            confirmLabel="Resume work"
            variant="primary"
            pending={resolve.isPending}
            testId="fire-resume"
            onConfirm={() => send('resume')}
          />
        </div>
        <div className="nx-fire-panel__choice">
          <ConfirmAction
            label={cancelAllLabel(event.interrupted + event.queued)}
            consequence={
              `Ends the freeze and puts the robots back in service, then CANCELS every task that is ` +
              `paused or running plus the queued ones (${event.interrupted} paused, ` +
              `${event.queued} queued). Cancelled work does not come back — it has to be ` +
              `dispatched again by hand.`
            }
            confirmLabel="Cancel everything"
            variant="danger"
            pending={resolve.isPending}
            testId="fire-cancel-all"
            onConfirm={() => send('cancel_all')}
          />
        </div>
      </div>

      {resolve.isError && (
        <p className="nx-fire-panel__error" role="alert" data-testid="fire-resolve-error">
          {fireErrorText(resolve.error, 'resolve')}
        </p>
      )}
    </div>
  );
}

export function FireModePanel({ onClose }: { onClose: () => void }) {
  const state = useFireModeState();
  // THE REPORT OUTLIVES THE EVENT. It used to live inside the recovery
  // form, which renders only while the event is active -- and a
  // successful resolve invalidates the fire state, whose refetch says
  // "not active" a few milliseconds later. The report, including the
  // list of tasks that did NOT come back, unmounted before anyone could
  // read it and the panel fell back to "No fire event" and the trigger
  // form. Held here until the panel closes.
  const [resolved, setResolved] = useState<FireResolveResponse | null>(null);
  const active = state.data?.active === true && resolved === null;
  const event = state.data?.event ?? null;

  // Focus moves INTO the dialog when it opens and back to whatever had
  // it when it closes. Opened from the avatar menu, the menu item that
  // had focus unmounts with the menu, so focus fell to <body>: a
  // keyboard user's next Tab went to the page behind the dialog.
  const dialogRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => {
      if (before && before.isConnected && typeof before.focus === 'function') before.focus();
    };
  }, []);

  // A third way out, matching every other dialog in the app
  // (`ConfirmDialog`, `TaskModal`, `OccurrenceScopeDialog`). Closing the
  // panel never touches the freeze -- it is held by the server and ends
  // only through `resolve` -- so there is nothing to lose by letting an
  // operator dismiss a panel they opened by mistake.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div
      className="nx-fire-panel__backdrop"
      // Same opt-out marker `ConfirmDialog` carries: this renders into
      // document.body, so without it every drawer using
      // `useDismissOnOutside` treats a press in here as an outside
      // press and unmounts before the click lands.
      data-nx-modal-layer=""
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`nx-fire-panel${active ? ' nx-fire-panel--active' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="nx-fire-panel-title"
        data-testid="fire-mode-panel"
        ref={dialogRef}
        tabIndex={-1}
      >
        <div className="nx-fire-panel__head">
          <h2 id="nx-fire-panel-title">Emergency — fire mode</h2>
          <button
            type="button"
            className="nx-fire-panel__close"
            aria-label="Close"
            onClick={onClose}
            data-testid="fire-panel-close"
          >
            ×
          </button>
        </div>

        {state.isLoading && <p className="nx-fire-panel__muted">Reading fire state…</p>}

        {state.isError && (
          // A way to try again, and no reference to a "below" that is not
          // there: on a failed read this sentence is the whole panel.
          <div className="nx-fire-panel__error" role="alert" data-testid="fire-state-error">
            <p>
              Could not read the fire state for this site. A freeze, if there is one, is held by
              the server and is unaffected by this; the building&apos;s fire panel still controls
              it.
            </p>
            <button
              type="button"
              className="nx-fire-panel__btn"
              onClick={() => void state.refetch()}
              disabled={state.isFetching}
              data-testid="fire-state-retry"
            >
              {state.isFetching ? 'Reading…' : 'Try again'}
            </button>
          </div>
        )}

        {resolved && <ResolutionReport result={resolved} />}

        {state.data && !active && !resolved && (
          <>
            <p className="nx-fire-panel__status" data-testid="fire-state-idle">
              <strong>No fire event.</strong> Dispatch is running normally at this site.
            </p>
            <TriggerForm onDone={() => undefined} />
          </>
        )}

        {state.data && active && (
          <>
            <p className="nx-fire-panel__status" data-testid="fire-state-active">
              <strong>
                {event?.drill ? 'FIRE DRILL ACTIVE' : 'FIRE MODE ACTIVE'}
              </strong>{' '}
              — dispatch is held for this site.
              {event ? ` ${triggerSummary(event)}.` : ''}
            </p>
            <dl className="nx-fire-panel__counts">
              <div>
                <dt>Paused tasks</dt>
                <dd data-testid="fire-interrupted-count">{state.data.interrupted_task_count}</dd>
              </div>
              <div>
                <dt>Queued behind the freeze</dt>
                <dd data-testid="fire-queued-count">{state.data.queued_task_count}</dd>
              </div>
            </dl>
            <RecoveryForm
              event={{
                interrupted: state.data.interrupted_task_count,
                queued: state.data.queued_task_count,
              }}
              onResolved={setResolved}
            />
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
